import { useMemo } from "react";
import { CardBody, CardTitle, FormGroup, Input, Label } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignLeft, faQrcode } from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import PlaylistData from "../../../playlist/data/PlaylistData";
import { SongbookMapExtractor } from "../../../playlist/share/SongbookMapExtractor";
import { PreferredSongnumberFinder } from "../../../playlist/share/PreferredSongnumberFinder";
import { PlaylistFromUrlImportParser } from "../../../playlist/import/url/PlaylistFromUrlImportParser";
import { RoutePaths } from "../../../RoutePaths";
import { UiConfig } from "../../../Config";
import { ToggleButton } from "../../../common/ToggleButton";
import { CopyToClipboardButton } from "../../../common/CopyToClipboardButton";
import { LookupSongFunction } from "../../../common/LookupSongFunction";
import useToggleValueWithLocalStoragePersistence from "../../../common/hooks/useToggleValueWithLocalStoragePersistence";
import { useStateWithLocalStoragePersistence } from "../../../common/hooks/useStateWithLocalStoragePersistenceAndDefault";
import { LocalStorageKey } from "../../../localStoragePersistence/common/LocalStorageKey";
import { SongUb } from "../../../gql/graphql";

interface Props {
  playlistIdentifier: string;
  playlistData: PlaylistData;
  lookupSong: LookupSongFunction;
}

const PERSISTENCE_GROUP = "SharePlaylist";
const showTonalityKey = new LocalStorageKey(PERSISTENCE_GROUP, "showTonality");
const showSongNumbersKey = new LocalStorageKey(
  PERSISTENCE_GROUP,
  "showSongNumbers",
);
const currentSongbookIdKey = new LocalStorageKey(
  PERSISTENCE_GROUP,
  "currentSongbookId",
);

export function SharePlaylistSidebarBody({
  playlistIdentifier,
  playlistData,
  lookupSong,
}: Props) {
  const { t } = useTranslation();

  const [showTonality, setShowTonality] =
    useToggleValueWithLocalStoragePersistence(showTonalityKey, false);
  const [showSongNumbers, setShowSongNumbers] =
    useToggleValueWithLocalStoragePersistence(showSongNumbersKey, false);
  const [currentSongbookId, setCurrentSongbookId] =
    useStateWithLocalStoragePersistence<string>(currentSongbookIdKey);

  const songbookMap = useMemo(
    () =>
      new SongbookMapExtractor().extractSongbooks(
        playlistData.getAllValidSongEntries(lookupSong),
      ),
    [playlistData, lookupSong],
  );

  const importLink = generateImportLink(playlistData, playlistIdentifier);

  const text = useMemo(
    () =>
      generateText(
        playlistData,
        lookupSong,
        showTonality,
        showSongNumbers,
        currentSongbookId,
        t,
      ) +
      "\n\n" +
      t("playlist.share.importPrefix") +
      importLink,
    [
      playlistData,
      lookupSong,
      showTonality,
      showSongNumbers,
      currentSongbookId,
      importLink,
      t,
    ],
  );

  return (
    <>
      <CardBody>
        <CardTitle>
          <FontAwesomeIcon icon={faAlignLeft} className="me-1" />{" "}
          {t("playlist.share.shareAsTextTitle")}
        </CardTitle>
        <p>{t("playlist.share.shareAsTextDesc")}</p>
        <div className="d-flex flex-wrap gap-2 mb-3">
          <ToggleButton
            value={t("playlist.share.showTonality")}
            setIsActive={setShowTonality}
            isActive={showTonality}
            color="primary"
            outline
          />
          <ToggleButton
            value={t("playlist.share.showSongNumbers")}
            setIsActive={setShowSongNumbers}
            isActive={showSongNumbers}
            color="primary"
            outline
          />
        </div>
        {showSongNumbers && songbookMap.size > 0 && (
          <FormGroup>
            <Label for="sharePreferredSongbook">
              {t("playlist.share.preferredSongbook")}
            </Label>
            <Input
              id="sharePreferredSongbook"
              type="select"
              value={currentSongbookId ?? ""}
              onChange={(e) => setCurrentSongbookId(e.target.value)}
            >
              <option value="" disabled />
              {Array.from(songbookMap.entries()).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}
        <CopyToClipboardButton
          className="mb-2"
          buttonValue={t("playlist.copyContent")}
          content={text}
        />
        <Input
          type="textarea"
          rows={15}
          readOnly
          value={text}
          style={{ tabSize: 16 }}
        />
      </CardBody>
      <CardBody>
        <CardTitle>
          <FontAwesomeIcon icon={faQrcode} className="me-1" />{" "}
          {t("playlist.share.shareQrTitle")}
        </CardTitle>
        <p>{t("playlist.share.shareQrDesc")}</p>
        {/* white margin, so the code stays scannable in dark mode */}
        <div className="d-inline-block bg-white p-2">
          <QRCodeSVG value={importLink} size={250} />
        </div>
      </CardBody>
    </>
  );
}

function generateImportLink(
  playlistData: PlaylistData,
  identifier: string,
): string {
  //valid and invalid entries
  const songIds = playlistData
    .getItems()
    .map((entry) => entry.songEntryId)
    .filter((songEntryId) => !!songEntryId);
  return `${UiConfig.baseUrl}/${RoutePaths.IMPORT_PLAYLIST}/${identifier}?${PlaylistFromUrlImportParser.PARAM_KEY_NAME}=${songIds.join(",")}`;
}

function generateText(
  playlistData: PlaylistData,
  lookupSong: LookupSongFunction,
  showTonality: boolean,
  showSongNumbers: boolean,
  currentSongbookId: string | undefined,
  t: TFunction,
): string {
  return playlistData
    .getItems()
    .flatMap((entry) => {
      const song = lookupSong(entry.songEntryId);
      return song
        ? [
            generatePlaylistItem(
              song,
              showTonality,
              showSongNumbers,
              currentSongbookId,
            ),
          ]
        : [
            "---",
            t("playlist.item.invalidTitle") + " " + entry.songEntryId,
            t("playlist.item.invalidDesc"),
            "---",
          ];
    })
    .join("\n");
}

function generatePlaylistItem(
  song: SongUb,
  showTonality: boolean,
  showSongNumbers: boolean,
  currentSongbookId: string | undefined,
): string {
  return [
    "-",
    showSongNumbers &&
      new PreferredSongnumberFinder().findPreferredSongnumber(
        song,
        currentSongbookId,
      ),
    song.title,
    showTonality && "(" + song.tonality + ")",
  ]
    .filter((s): s is string => !!s)
    .join(" ");
}
